import React, { useEffect, useState } from "react";
import {
  Card, CardBody, Row, Col, Table, Button, Input,
  Modal, ModalHeader, ModalBody
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import { toast } from "react-toastify";
import moment from "moment";

const ReturnSales = () => {

  const dispatch = useDispatch();
  const user = useSelector(s => s.customer.customer);

  /* ================= DATA ================= */
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");

  /* ================= VIEW ================= */
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  /* ================= LOAD RETURNS ================= */
  const loadReturns = async (p = page) => {

    dispatch(loadingTrue());

    const res = await Handler({
      method: "post",
      url: "/sales/return_list.php",
      data: {
        outlet_id: user.outlet_id,
        page: p,
        limit,
        search
      }
    });

    dispatch(loadingFalse());

    if (!res?.success) {
      toast.error(res?.msg || "Failed to load return sales");
      return;
    }

    setRows(res.data.rows || []);
    setTotal(res.data.count || 0);
    setPage(p);
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    if (user?.outlet_id) loadReturns(1);
  }, [user?.outlet_id]);

  /* ================= SEARCH ================= */
  useEffect(() => {
    const delay = setTimeout(() => {
      loadReturns(1);
    }, 400); // debounce

    return () => clearTimeout(delay);
  }, [search]);

  /* ================= VIEW ================= */
  const openView = (row) => {
    setViewData(row);
    setViewOpen(true);
  };

  return (
    <>
      <Row className="mb-3">
        <Col md={6}>
          <h5 className="fw-bold">Return Sales</h5>
          <p className="text-muted mb-0">List of refunded / returned bills</p>
        </Col>

        <Col md={6} className="text-end">
          <Input
            placeholder="Search bill / customer / product"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: 280, display: "inline-block" }}
          />
        </Col>
      </Row>

      <Card className="shadow-sm">
        <CardBody className="p-0">
          <Table hover responsive className="mb-0 billing-table">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Return Bill</th>
                <th>Customer</th>
                <th>Outlet</th>
                <th className="text-end">Refund</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>

              {rows.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">
                    No return sales found
                  </td>
                </tr>
              )}

              {rows.map(r => (
                <tr key={r.return_sale_id}>
                  <td>{moment(r.return_date).format("DD-MM-YYYY")}</td>
                  <td><b>RET-{r.return_sale_id}</b></td>
                  <td>
                    {r.customer_name || "Walk-in"}<br />
                    <span className="small text-muted">
                      {r.customer_phone || "-"}
                    </span>
                  </td>
                  <td>{r.outlet_name}</td>
                  <td className="text-end fw-bold text-danger">
                    ₹ {Number(r.refund_amount).toFixed(2)}
                  </td>
                  <td className="text-center">
                    <Button
                      size="sm"
                      color="primary"
                      onClick={() => openView(r)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}

            </tbody>
          </Table>

          <Pagination
            page={page}
            total={total}
            limit={limit}
            onPrev={() => page > 1 && loadReturns(page - 1)}
            onNext={() => page * limit < total && loadReturns(page + 1)}
          />
        </CardBody>
      </Card>

      {/* ================= VIEW MODAL ================= */}
      <Modal isOpen={viewOpen} toggle={() => setViewOpen(false)} size="lg">
        <ModalHeader toggle={() => setViewOpen(false)}>
          Return Bill Details
        </ModalHeader>
        <ModalBody>

          {viewData && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <div><b>Return Bill:</b> RET-{viewData.return_sale_id}</div>
                  <div>
                    <b>Date:</b>{" "}
                    {moment(viewData.return_date).format("DD-MM-YYYY HH:mm")}
                  </div>
                </Col>
                <Col md={6}>
                  <div><b>Customer:</b> {viewData.customer_name || "-"}</div>
                  <div><b>Phone:</b> {viewData.customer_phone || "-"}</div>
                </Col>
              </Row>

              <Table bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Item</th>
                    <th className="text-end">Qty</th>
                    <th className="text-end">Rate</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {viewData.items.map((i, idx) => (
                    <tr key={idx}>
                      <td>{i.product_name}</td>
                      <td className="text-end">{i.quantity}</td>
                      <td className="text-end">₹ {Number(i.rate).toFixed(2)}</td>
                      <td className="text-end fw-bold">
                        ₹ {Number(i.amount).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="text-end fw-bold fs-5 text-danger">
                Refund Total: ₹ {Number(viewData.refund_amount).toFixed(2)}
              </div>
            </>
          )}

        </ModalBody>
      </Modal>
    </>
  );
};

export default ReturnSales;
